import {
  Card,
  CardHeader,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import * as React from 'react';
import { TableChart, Folder } from '@material-ui/icons';
import { exportAllDormantItems } from 'lib/Api/ProductStack';

const useStyles = makeStyles((theme) => ({
  clickableListItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pageHeading: {
    marginBottom: theme.spacing(3),
  },
}));

export const SpreadsheetsView: React.FC = () => {
  const classes = useStyles();
  const [exportLoading, setExportLoading] = React.useState(false);

  const handleExportAllDormantItems = async () => {
    setExportLoading(true);
    try {
      await exportAllDormantItems();
    } finally {
      setExportLoading(false);
    }
  };

  const recruiterShortcuts = [
    {
      link: 'https://docs.google.com/spreadsheets/d/1r2G6FKBpjQE_H_BiYDmL7USHlwOlr2v6pLnOPdRdYms/edit#gid=731692981',
      title: 'Data Monitoring',
      description:
        'Get an overview of important metrics such as overdue statuses.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/1HowuBRbrAXmmKmZ1mOpa9dtVOzIW3byCcTMGCjyoz3E/edit?gid=1054473647#gid=1054473647',
      title: 'Retaining Clients',
      description:
        'This sheet shows recruiters which clients can be re-engaged for potential vacancies by highlighting the last interaction date - either a vacancy closure or a successful placement.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/1V_bQkBuV9PcrZlxT8dRQ91_achpwBbuRh56dJQ9ndNk/edit#gid=1938325320',
      title: 'Handover Report',
      description:
        'Create a handover report for your team whilst you are away.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/1TCcVt6wFNAeBzD-wfaL4GcAQFSktreD6_foKZ4tJr_I/edit#gid=0',
      title: 'LinkedIn Schedule & Usage',
      description:
        'Schedule times when using LinkedIn Corporate and track your LinkedIn posts and jobs.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/1WIc6JBb1C6IfDi9qp1yOwOHeMzAcJZLSE2VgAB_vot8/edit#gid=1078260614',
      title: 'Post Placement Follow Up Sheet',
      description: 'Keep track of followups with candidates and clients.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/1hY8873c01-UVOXwMNfUWndAieJyDe2_kDjQCbsKdRTU/edit#gid=1742488263',
      title: 'Project Scouting & Project Hunting Workbook',
      description:
        'Explore job vacancies which Konnekt is currently not actively recruiting for but are advertised on external platforms. This helps you offer candidates a broader range of opportunities beyond our current, available positions.',
      icon: <TableChart />,
    },
    {
      link: 'https://docs.google.com/spreadsheets/d/19gJfzIwLfH16kgjb2l3_IpCa3mGcjMt4i9k1pg7eY1c/edit#gid=417179507',
      title: 'Pro-Rata Calculator',
      description: 'Calculate the pro-rata amount for a credit note.',
      icon: <TableChart />,
    },
    {
      link: 'https://intranet.exp42.com/dashboards#sim',
      title: 'Salariesinmalta.com Salary Brackets',
      description:
        'A list of positions and data from Salariesinmalta.com, with detailed salary brackets for each role.',
      icon: <TableChart />,
    },
    {
      divider: true,
    },
    {
      link: 'https://drive.google.com/drive/folders/1cnCRYxUPYrfcN5yWaGIoB40S1UvquMSM',
      title: 'Recruitment Procedures',
      description:
        'A folder containing all our procedures related to recruitment.',
      icon: <Folder />,
    },
  ];

  const operationsShortcuts = [
    {
      link: 'https://docs.google.com/spreadsheets/d/1TFBvB2CvLXHQ2SLFBBHXhw0jJwhKJST7k0sM8QYr-Qs/edit#gid=1966919949',
      title: 'Project Assignment Sheet',
      description:
        'Monitor recruiter capacity and assign or manage recruiter projects.',
      icon: <TableChart />,
    },
    {
      title: 'Talexio Dormant Client Report',
      description:
        'Export an audit report of all dormant activity across all Talexio clients.',
      icon: <TableChart />,
      onClick: handleExportAllDormantItems,
      disabled: exportLoading,
    },
  ];

  return (
    <>
      <Typography
        variant="h5"
        component={'h1' as React.ElementType}
        className={classes.pageHeading}
      >
        Tools &amp; Resources
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader
              title="Recruitment Utility"
              titleTypographyProps={{ component: 'h2' as React.ElementType }}
            />
            <List>
              {recruiterShortcuts.map((d, index) =>
                d.divider ? (
                  <Divider key={`divider_${index}`} />
                ) : (
                  <ListItem
                    key={d.title}
                    className={classes.clickableListItem}
                    onClick={() => {
                      window.open(d.link);
                    }}
                  >
                    {d.icon && <ListItemIcon>{d.icon}</ListItemIcon>}
                    <ListItemText primary={d.title} secondary={d.description} />
                  </ListItem>
                ),
              )}
            </List>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardHeader
              title="Operations Utility"
              titleTypographyProps={{ component: 'h2' as React.ElementType }}
            />
            <List>
              {operationsShortcuts.map((d) => (
                <ListItem
                  key={d.title}
                  className={classes.clickableListItem}
                  onClick={() => {
                    if (d.link) {
                      window.open(d.link);
                    } else if (d.onClick) {
                      d.onClick();
                    }
                  }}
                  disabled={d.disabled}
                >
                  {d.icon && <ListItemIcon>{d.icon}</ListItemIcon>}
                  <ListItemText
                    primary={d.disabled ? 'Downloading...' : d.title}
                    secondary={d.description}
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
